import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { SUBSCRIPTION_PLANS } from '@/config/subscriptionPlans';
import { SubscriptionPlan, SubscriptionPeriod } from '@/types/subscription';
import AlipayQRCode from '@/components/payment/AlipayQRCode';
import { EnhancedPaymentStatusMonitor } from '@/components/payment/EnhancedPaymentStatusMonitor';
import request from '@/api/request';
import { PaymentSuccessHandler } from '@/components/payment/PaymentSuccessHandler';
import { PaymentStatusRecovery } from '@/components/payment/PaymentStatusRecovery';
import { Header } from '@/components/landing/Header';
import { AdminOrderRepair } from '@/components/payment/AdminOrderRepair';
import { BufPayService } from '@/services/bufpayService';
import { SubscriptionUpgradeService } from '@/services/subscriptionUpgradeService';
import SubscriptionUpgradeDialog from '@/components/subscription/SubscriptionUpgradeDialog';
import { PaymentResponse } from '@/types/payment';
import { PaymentQRCode } from '@/components/payment/PaymentQRCode';
import { logger } from '@/utils/logger';
import { DynamicPricingService, PricingContext } from '@/services/dynamicPricingService';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { PaymentModal } from '@/components/payment/PaymentModal';
import { PaymentModalData } from '@/types/payment-modal';
import {
  ArrowLeft,
  Check,
  CreditCard,
  Star,
  Crown,
  Zap,
  Percent,
  Home,
  ChevronRight,
  TrendingUp,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import {
  getPaymentCenterAccessTime,
  isInPromoPeriod,
  calculateRemainingTime,
  formatTimeLeft as formatTimeLeftUtil,
  shouldShowPromoOffer,
  recordPaymentCenterAccess
} from "@/utils/paymentTimer";
import { getUserTier } from "@/utils/subscriptionUtils";
import { paymentStatusService } from '@/services/paymentStatusService';
// 已删除creemOptimizer导入，直接使用Creem API

// 原价（月付）展示映射
const ORIGINAL_MONTHLY_PRICE: Record<'trial' | 'pro' | 'premium', number> = {
  trial: 19,
  pro: 59,
  premium: 139
};

const CreemAlipayQRCode: React.FC<{
  priceId: string;
  planName: string;
  price: number;
}> = ({
  priceId,
  planName,
  price,
}) => { return (
    <AlipayQRCode
      priceId={priceId }
      title={`${planName} - $$`}
      showPrice={true}
    />
  );
};

function getCreemPriceId(plan: SubscriptionPlan, period: SubscriptionPeriod): string {
  if (plan.tier === "pro" && period === "monthly") return "prod_3nJOuQeVStqkp6JaDcrKHf";
  if (plan.tier === "pro" && period === "yearly") return "prod_5qBlDTLpD3h9gvOZFd4Rgu";
  if (plan.tier === "premium" && period === "monthly") return "prod_4HYBfvrcbXYnbxjlswMj28";
  if (plan.tier === "premium" && period === "yearly") return "prod_6OfIoVnRg2pXsuYceVKOYk";
  return "";
}

export default function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user: currentUser, isAuthenticated: currentIsAuthenticated, updateUser } = useAuth();
  const { t } = useTranslation();
  const { primaryStatus, hasActiveSubscription, allSubscriptions, refresh: refreshSubscriptionStatus } = useSubscriptionStatus();

  // 获取用户当前等级 - 优先使用订阅状态钩子的数据
  const userCurrentTier = (() => {
    // 如果有活跃订阅，使用订阅状态数据中的tier字段
    if (hasActiveSubscription && primaryStatus?.status === 'active' && primaryStatus.tier) {
      return primaryStatus.tier; // 使用后端API返回的tier字段
    }
    // 否则使用用户对象的等级
    return getUserTier(currentUser);
  })();

  // 获取来源操作（续费/升级）
  const locationState = location.state as {
    action?: 'renew' | 'upgrade';
    currentSubscription?: any;
  } | null;

  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<SubscriptionPeriod>('monthly');
  const [showQRCode, setShowQRCode] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showPromoOffer, setShowPromoOffer] = useState(false);
  const paymentInfoRef = useRef<HTMLDivElement>(null);

  // 支付状态管理
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'pending' | 'paid' | 'failed'>('idle');
  const [currentCheckout, setCurrentCheckout] = useState<any>(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // BufPay 支付状态
  const [bufpayPaymentInfo, setBufpayPaymentInfo] = useState<PaymentResponse | null>(null);
  const [bufpayOrderId, setBufpayOrderId] = useState<string | null>(null);

  // 倒计时效果（包含毫秒）
  const [timeLeftMs, setTimeLeftMs] = useState(0);

  // 支付模态框状态
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState<PaymentModalData | null>(null);

  /**
   * 检测未完成的支付并提示恢复
   */
  useEffect(() => {
    const pendingPaymentStr = localStorage.getItem('pending_payment');
    if (pendingPaymentStr) {
      try {
        const pendingPayment = JSON.parse(pendingPaymentStr);
        const { orderId, amount, qrCode, qrImage, timestamp } = pendingPayment;

        // 检查是否在30分钟内
        const thirtyMinutes = 30 * 60 * 1000;
        if (Date.now() - timestamp < thirtyMinutes) {
          // 提示用户恢复支付
          const continuePayment = async () => {
            try {
              // 查询订单状态
              const status = await BufPayService.checkOrderStatus(orderId);
              if (status.isPaid) {
                toast({
                  title: '该订单已支付成功！',
                  description: '订阅已激活'
                });
                localStorage.removeItem('pending_payment');
              } else {
                // 恢复支付模态框 - 使用保存的数据
                setPaymentModalData({
                  state: 'waiting_scan',
                  orderId,
                  qrCode: qrCode || '',
                  qrImage: qrImage || '',
                  amount: amount || 0,
                  timeLeft: 300,
                  retryCount: 0
                });
                setShowPaymentModal(true);
              }
            } catch (error) {
              console.error('恢复支付失败:', error);
              localStorage.removeItem('pending_payment');
            }
          };

          toast({
            title: '检测到未完成的支付',
            description: '是否继续之前的支付？'
          });

          // 自动尝试恢复
          continuePayment();
        } else {
          // 超过30分钟，清除记录
          localStorage.removeItem('pending_payment');
        }
      } catch (error) {
        console.error('解析pending_payment失败:', error);
        localStorage.removeItem('pending_payment');
      }
    }
  }, []);

  // 升级相关状态
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [currentSubscriptionTier, setCurrentSubscriptionTier] = useState<string | null>(null);

  // 动态价格状态
  const [dynamicPricing, setDynamicPricing] = useState<any>(null);
  const [pricingContext, setPricingContext] = useState<PricingContext | null>(null);

  // 记录用户首次访问订阅中心 + 检查是否应该显示限时优惠
  useEffect(() => {
    const initPromoOffer = async () => {
      if (currentUser?.id) {
        // 1. 记录首次访问时间（如果是首次访问）
        recordPaymentCenterAccess(currentUser.id);

        // 2. 获取用户订阅等级
        const userTier = getUserTier(currentUser);

        // 3. 检查是否应该显示优惠（传入userTier避免重复API调用）
        const shouldShow = await shouldShowPromoOffer(currentUser.id, userTier);
        setShowPromoOffer(shouldShow);

        console.log('💰 限时优惠检查结果:', {
          userId: currentUser.id,
          userTier,
          shouldShow
        });
      } else {
        setShowPromoOffer(false);
      }
    };

    initPromoOffer();
  }, [currentUser?.id]);

  // 🔧 NEW: 从 URL 参数读取预选的计划和周期
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tierParam = searchParams.get('tier');
    const periodParam = searchParams.get('period');

    // 优先使用 URL 参数
    if (tierParam) {
      const plan = SUBSCRIPTION_PLANS.find(p => p.tier === tierParam);
      if (plan) {
        setSelectedPlan(plan);
        console.log('🔗 从 URL 参数自动选择计划:', plan.name);
      }
    } else {
      // 兜底：从 localStorage 读取预选的计划
      const savedPlanTier = localStorage.getItem('selectedPlan');
      if (savedPlanTier) {
        const plan = SUBSCRIPTION_PLANS.find(p => p.tier === savedPlanTier);
        if (plan) {
          setSelectedPlan(plan);
          console.log(t('payment.console.autoSelectPlan'), plan.name);
        }
        // 清除localStorage中的选择，避免重复使用
        localStorage.removeItem('selectedPlan');
      }
    }

    // 设置订阅周期
    if (periodParam === 'monthly' || periodParam === 'yearly') {
      setSelectedPeriod(periodParam);
      console.log('🔗 从 URL 参数自动选择周期:', periodParam);
    }
  }, [location.search]);

  useEffect(() => {
    if (!currentUser?.id) return;

    const updateTimer = () => {
      const remainingMs = calculateRemainingTime(currentUser.id);
      setTimeLeftMs(remainingMs);
      setTimeLeft(Math.floor(remainingMs / 1000)); // 保持秒数用于其他逻辑
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000); // 🔧 PERF FIX: 优化从100ms到1000ms，避免过度渲染

    return () => clearInterval(interval);
  }, [currentUser?.id]);

  // 支付状态恢复
  useEffect(() => {
    const activePayments = paymentStatusService.getActivePaymentStatuses();
    if (activePayments.length > 0) {
      setShowRecovery(true);
    }
  }, []);

  // 检查用户订阅状态并计算动态价格
  useEffect(() => {
    const calculateDynamicPricing = async () => {
      if (!currentUser?.id || !selectedPlan) return;

      try {
        // 获取当前订阅信息
        let currentSubscription = null;
        let currentSubscriptionTier = null;

        if (hasActiveSubscription && allSubscriptions.length > 0) {
          // 找到最高级别的活跃订阅
          const activeSubscription = allSubscriptions.find(sub => sub.subscriptionType === 'premium') || allSubscriptions[0];

          if (activeSubscription) {
            currentSubscriptionTier = activeSubscription.subscriptionType;
            // 构造订阅对象供补差价计算使用
            currentSubscription = {
              tier: activeSubscription.subscriptionType,
              expires_at: primaryStatus.expiresAt || new Date().toISOString(),
              started_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 假设30天前开始
              id: activeSubscription.subscriptionId
            };
          }
        }

        setCurrentSubscriptionTier(currentSubscriptionTier);

        // 确定操作类型
        let action: 'new' | 'renew' | 'upgrade' | 'prorated_upgrade' = 'new';

        if (hasActiveSubscription && currentSubscription) {
          // 检查是否是升级
          const tierLevels = { trial: 0, pro: 1, professional: 1, premium: 2 };
          const currentLevel = tierLevels[currentSubscriptionTier as keyof typeof tierLevels] || 0;
          const targetLevel = tierLevels[selectedPlan.tier as keyof typeof tierLevels] || 0;

          if (targetLevel > currentLevel) {
            // 升级操作，默认使用补差价
            action = 'upgrade';
          } else {
            action = 'renew';
          }
        }

        const context: PricingContext = {
          userId: currentUser.id,
          action,
          targetTier: selectedPlan.tier,
          targetPeriod: selectedPeriod,
          currentSubscription: currentSubscription ? {
            ...currentSubscription,
            expires_at: typeof currentSubscription.expires_at === 'string'
              ? currentSubscription.expires_at
              : currentSubscription.expires_at.toISOString()
          } : undefined,
          allowManualAmount: false // 默认不允许手动输入
        };

        const pricing = await DynamicPricingService.calculatePrice(context);
        setDynamicPricing(pricing);
        setPricingContext(context);

      } catch (error) {
        logger.error('计算动态价格失败:', error);
      }
    };

    calculateDynamicPricing();
  }, [
    currentUser?.id,
    selectedPlan?.id,
    selectedPlan?.tier,
    selectedPeriod,
    hasActiveSubscription,
    allSubscriptions.length,
    primaryStatus?.status,
    primaryStatus?.tier,
    primaryStatus?.expiresAt,
    locationState?.action
  ]);

  // 页面访问时记录时间（用于限时优惠）
  useEffect(() => {
    if (currentUser?.id) {
      // 获取或创建支付中心访问时间
      const accessTime = getPaymentCenterAccessTime(currentUser.id);
      console.log(t('payment.console.accessTime'), accessTime?.toLocaleString());

      // 立即更新倒计时
      const remainingMs = calculateRemainingTime(currentUser.id);
      const remainingSeconds = Math.floor(remainingMs / 1000);
      setTimeLeft(remainingSeconds);

      console.log(t('payment.console.remainingTime'), remainingSeconds, t('payment.console.seconds'));
    }
  }, [currentUser?.id]);

  // 处理计划选择（仅选中并滚动，不自动弹窗）
  const handlePlanSelect = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setShowQRCode(false);

    // 自动滚动到支付信息区域（考虑固定Header偏移）
    setTimeout(() => {
      const paymentSection = document.getElementById('payment-section');
      if (paymentSection) {
        const headerOffset = 72; // 头部大致高度（px）
        const rect = paymentSection.getBoundingClientRect();
        const y = rect.top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    }, 300); // 延迟以确保状态更新与布局完成
  };

  // 处理支付
  const handlePayment = async () => {
    if (!selectedPlan || !currentUser) return;

    // 检查是否为升级操作
    if (hasActiveSubscription && currentSubscriptionTier) {
      const tierLevels = { trial: 0, pro: 1, premium: 2 };
      const currentLevel = tierLevels[currentSubscriptionTier as keyof typeof tierLevels] || 0;
      const targetLevel = tierLevels[selectedPlan.tier as keyof typeof tierLevels] || 0;

      if (targetLevel > currentLevel) {
        // 这是升级操作，显示升级对话框
        setShowUpgradeDialog(true);
        return;
      }
    }

    try {
      setIsCreatingCheckout(true);
      setCheckoutError(null);

      // 使用 BufPay 创建支付订单
      const paymentRequest = {
        userId: currentUser.id,
        userEmail: currentUser.email || '',
        productName: selectedPlan.name,
        productType: selectedPlan.tier === 'pro' ? 'professional' : selectedPlan.tier as 'professional' | 'premium',
        durationType: selectedPeriod,
        amount: getCurrentPrice(),
        payType: 'alipay' as const,
        // 传递价格上下文用于服务端验证
        pricingContext
      };

      const { orderId, paymentInfo } = await BufPayService.createPayment(paymentRequest);

      setBufpayOrderId(orderId);
      setBufpayPaymentInfo(paymentInfo);
      setPaymentStatus('pending');

      // 打开支付模态框
      setPaymentModalData({
        state: 'waiting_scan',
        orderId,
        qrCode: paymentInfo.qr || paymentInfo.htmlContent || '',
        qrImage: paymentInfo.qr_img || '',
        amount: getCurrentPrice(),
        timeLeft: 300, // 5分钟
        retryCount: 0,
        paymentInfo
      });
      setShowPaymentModal(true);

      toast({
        title: t('payment.messages.orderCreated'),
        description: '请使用支付宝扫码支付',
      });
    } catch (error: any) {
      console.error('Payment error:', error);
      setCheckoutError(error.message || t('payment.messages.orderCreateFailed'));
      toast({
        title: t('payment.messages.paymentFailed'),
        description: error.message || t('payment.messages.tryAgainLater'),
        variant: "destructive",
      });
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  // 获取当前价格（使用动态价格服务）
  const getCurrentPrice = () => {
    if (dynamicPricing) {
      return dynamicPricing.finalAmount;
    }

    // 兜底逻辑
    if (!selectedPlan) return 0;

    const pricing = selectedPeriod === 'monthly' ? selectedPlan.monthly : selectedPlan.yearly;
    const originalPrice = pricing.originalPrice;

    const isInDiscount = showPromoOffer && timeLeft > 0;

    return isInDiscount ? (pricing.discountPrice || originalPrice) : originalPrice;
  };

  // 获取原价
  const getOriginalPrice = () => {
    if (dynamicPricing) {
      return dynamicPricing.originalPrice;
    }

    if (!selectedPlan) return 0;

    const pricing = selectedPeriod === 'monthly' ? selectedPlan.monthly : selectedPlan.yearly;
    return pricing.originalPrice;
  };

  // 获取节省金额
  const getSavedAmount = () => {
    if (dynamicPricing) {
      return dynamicPricing.discountAmount;
    }

    const originalPrice = getOriginalPrice();
    const currentPrice = getCurrentPrice();
    return originalPrice - currentPrice;
  };

  // 获取年付节省金额（相比月付）
  const getYearlySavings = (plan: SubscriptionPlan) => {
    const monthlyTotal = ((ORIGINAL_MONTHLY_PRICE[plan.tier as 'trial' | 'pro' | 'premium']) || 0) * 12;
    const yearlyPrice = plan.yearly.originalPrice;
    return Math.max(0, monthlyTotal - yearlyPrice);
  };

  const getYearlySavingsPercent = (plan: SubscriptionPlan) => {
    const monthlyTotal = ((ORIGINAL_MONTHLY_PRICE[plan.tier as 'trial' | 'pro' | 'premium']) || 0) * 12;
    const savings = getYearlySavings(plan);
    if (monthlyTotal <= 0) return 0;
    return Math.round((savings / monthlyTotal) * 100);
  };

  // 处理支付成功
  const handlePaymentSuccess = (paymentData?: any) => {
    setPaymentStatus('paid');
    if (paymentData) {
      setCurrentCheckout(paymentData);

      // 更新支付状态
      paymentStatusService.savePaymentStatus(paymentData.id, {
        status: 'paid',
        message: t('payment.messages.paymentSuccess'),
        progress: 100,
        amount: paymentData.amount,
        currency: paymentData.currency,
        paidAt: new Date().toISOString(),
      });
    }

    // 🆕 触发支付成功事件，强制刷新订阅状态
    // 传入期望的订阅等级，用于验证
    const expectedTier = selectedPlan?.tier;
    logger.info('💳 触发支付成功事件', { expectedTier });
    window.dispatchEvent(new CustomEvent('paymentSuccess', {
      detail: { expectedTier }
    }));

    toast({
      title: t('payment.messages.paymentSuccess'),
      description: t('payment.messages.upgrading'),
      duration: 3000,
    });

    // 关闭模态框
    setShowPaymentModal(false);
    setPaymentModalData(null);
  };

  // 处理支付模态框关闭
  const handlePaymentModalClose = () => {
    console.log('🚪 关闭支付模态框');
    setShowPaymentModal(false);
    setPaymentModalData(null);
  };

  // 处理支付取消
  const handlePaymentCancel = () => {
    console.log('🔴 处理支付取消');

    // 重置所有支付相关状态
    setPaymentStatus('idle');
    setBufpayPaymentInfo(null);
    setBufpayOrderId(null);
    setShowQRCode(false);
    setShowPaymentModal(false);
    setPaymentModalData(null);

    // 清理localStorage
    localStorage.removeItem('pending_payment');

    toast({
      title: '已取消支付',
      description: '您可以随时返回继续支付',
    });

    console.log('✅ 支付状态已重置');
  };

  // 处理支付失败
  const handlePaymentFailed = (error: string) => {
    setPaymentStatus('failed');

    if (currentCheckout) {
      paymentStatusService.savePaymentStatus(currentCheckout.id, {
        status: 'failed',
        message: error,
        progress: 0,
        error: error,
      });
    }

    toast({
      title: t('payment.messages.paymentFailed'),
      description: error,
      variant: "destructive",
    });
  };

  // 处理支付过期
  const handlePaymentExpired = () => {
    if (currentCheckout) {
      paymentStatusService.savePaymentStatus(currentCheckout.id, {
        status: 'expired',
        message: t('payment.messages.paymentExpired'),
        progress: 0,
      });
    }

    toast({
      title: t('payment.messages.paymentExpired'),
      description: t('payment.messages.recreateOrder'),
      variant: "destructive",
    });
  };

  // BufPay 支付成功处理
  const handleBufpaySuccess = async () => {
    logger.info('BufPay支付成功', { orderId: bufpayOrderId });
    setPaymentStatus('paid');

    // 🆕 触发支付成功事件，强制刷新订阅状态
    // 传入期望的订阅等级，用于验证
    const expectedTier = selectedPlan?.tier;
    logger.info('💳 触发支付成功事件（BufPay）', { expectedTier });
    window.dispatchEvent(new CustomEvent('paymentSuccess', {
      detail: { expectedTier }
    }));

    // 立即显示成功提示
    toast({
      title: t('payment.messages.paymentSuccess'),
      description: t('payment.messages.upgrading'),
      duration: 5000,
    });

    // 清理支付状态
    setShowQRCode(false);
    setBufpayPaymentInfo(null);

    // 立即执行数据清理和状态刷新
    try {
      if (currentUser) {
        // 先清理可能导致验证失败的数据
        const { PaymentDataCleanupService } = await import('@/services/paymentDataCleanupService');
        PaymentDataCleanupService.performCompleteCleanup(currentUser.id);

        // 等待清理完成
        await new Promise(resolve => setTimeout(resolve, 300));

        logger.info(t('pages.messages.支付成功后数据清理完成'));

        // 刷新订阅状态
        logger.info('刷新订阅状态...');
        await refreshSubscriptionStatus();
        logger.info(t('pages.messages.订阅状态刷新完成'));

        // 强制刷新用户数据（触发认证状态更新）
        try {
          logger.info('强制刷新用户数据...');
          await updateUser({}); // 空对象触发用户数据重新获取
          logger.info(t('pages.messages.用户数据刷新完成'));
        } catch (error) {
          logger.warn('用户数据刷新失败:', error);
        }

        // 🔧 FIX: 触发全局支付成功事件，通知所有组件刷新状态
        window.dispatchEvent(new CustomEvent('paymentSuccess', {
          detail: {
            userId: currentUser.id,
            timestamp: Date.now()
          }
        }));
        logger.info(t('pages.messages.已触发全局支付成功事件'));

        // 重新检查限时优惠状态
        const shouldShow = await shouldShowPromoOffer(currentUser.id);
        setShowPromoOffer(shouldShow);
        logger.info('限时优惠状态已更新:', { shouldShow });

        // 强制触发页面状态更新
        window.dispatchEvent(new Event('subscription-updated'));
      }
    } catch (error) {
      logger.warn('数据清理或状态刷新失败:', error);
    }

    // 增加更多时间确保状态刷新完成
    setTimeout(() => {
      // 使用window.location.href确保完整页面刷新
      window.location.href = `/payment/result?order_id=${bufpayOrderId}&status=success`;
    }, 5000);
  };

  // BufPay 支付超时处理
  const handleBufpayTimeout = () => {
    setPaymentStatus('failed');
    setBufpayPaymentInfo(null);
    setBufpayOrderId(null);
    setShowQRCode(false);

    toast({
      title: t('payment.messages.paymentTimeout'),
      description: t('payment.messages.recreateOrder'),
      variant: "destructive",
    });
  };

  // BufPay 支付错误处理
  const handleBufpayError = (error: string) => {
    logger.error('BufPay支付错误', { error, orderId: bufpayOrderId });
    setPaymentStatus('failed');

    toast({
      title: t('payment.messages.paymentFailed'),
      description: error,
      variant: "destructive",
      duration: 8000,
    });
  };

  // 如果支付成功，显示成功处理页面
  if (paymentStatus === 'paid' && currentCheckout) {
    return (
      <div className="payment-page payment-container min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
        <div className="container mx-auto max-w-4xl">
          <PaymentSuccessHandler
            paymentData={currentCheckout}
            onComplete={() => navigate('/user-profile')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page payment-container min-h-screen bg-background" style={{ paddingTop: '64px' }}>
      {/* 统一Header */}
      <Header />
      {/* 管理员批量修复工具（仅在 ?adminRepair=1 时显示） */}
      {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('adminRepair') === '1' && (
        <div className="container mx-auto px-4 mt-4">
          <AdminOrderRepair />
        </div>
      )}

      {/* 页面内容 */}
      <div className="container mx-auto px-4 py-20 space-y-12">
        {/* 页面标题 */}
        <div className="text-center mb-8 mt-12">
          <div className="mb-6 flex flex-col items-center">
            <h1 className="text-4xl font-bold text-foreground mb-4 block">{t('payment.title')}</h1>
            <p className="text-lg text-muted-foreground block">{t('payment.description')}</p>
          </div>
        </div>
        {/* 支付状态恢复 */}
        {showRecovery && (
          <div className="mb-8">
            <PaymentStatusRecovery
              onRecoveryComplete={() => {
                setShowRecovery(false);
                navigate('/user-profile');
              }}
              onNoActivePayments={() => setShowRecovery(false)}
            />
          </div>
        )}

        {/* 订阅周期切换 - 优化版本 */}
        <div className="flex justify-center mb-4">
          <div className="flex items-center gap-3 bg-background/90 backdrop-blur-sm rounded-2xl p-3 shadow-xl border border-border max-w-4xl">
            {/* 按月订阅 */}
            <Button
              onClick={() => setSelectedPeriod('monthly')}
              className={`payment-button-monthly ${selectedPeriod === 'monthly' ? 'payment-button-active' : 'payment-button-inactive'}`}
            >
              {t('payment.billing.monthly')}
            </Button>

            {/* 切换按钮 - 使用 Switch 组件 */}
            <Switch
              checked={selectedPeriod === "yearly"}
              onCheckedChange={(checked) => setSelectedPeriod(checked ? "yearly" : "monthly")}
              className="mx-4"
            />

            {/* 按年订阅 */}
            <div className="relative">
              {/* 推荐标签 */}
              <div className="absolute -top-4 -right-3 z-20">
                <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-background shadow-xl px-3 py-1 text-xs font-bold rounded-full border-2 border-white animate-bounce">
                  💰 {t('payment.labels.savings')}
                </Badge>
              </div>

              <Button
                onClick={() => setSelectedPeriod('yearly')}
                className={`payment-button-yearly ${selectedPeriod === 'yearly' ? 'payment-button-yearly-active' : 'payment-button-yearly-inactive'}`}
              >
                <span className="relative z-10 drop-shadow-sm">
                  {t('payment.billing.yearly')} <span className="text-xs ml-1 font-extrabold text-yellow-200">({t('payment.billing.savingsPercent')})</span>
                </span>
                {selectedPeriod === 'yearly' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 via-orange-400/20 to-red-400/20 animate-pulse"></div>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* 限时优惠倒计时 */}
        {currentUser?.id && showPromoOffer && timeLeft > 0 && (
          <div className="text-center mb-8">
            <div className="bg-gradient-to-r from-red-500 to-pink-500 text-background px-8 py-6 rounded-2xl shadow-xl max-w-lg mx-auto">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Zap className="h-4 w-4 animate-pulse" />
                <span className="text-sm font-medium">{t('payment.billing.limitedTimeOffer')}</span>
              </div>

              {/* 突出显示的倒计时（包含毫秒） */}
              <div className="text-4xl md:text-5xl font-mono font-black tracking-wider mb-3">
                <span className="inline-block min-w-[2ch]">{Math.floor(timeLeftMs / 3600000).toString().padStart(2, '0')}</span>
                <span className="text-2xl md:text-3xl mx-1 opacity-80">:</span>
                <span className="inline-block min-w-[2ch]">{Math.floor((timeLeftMs % 3600000) / 60000).toString().padStart(2, '0')}</span>
                <span className="text-2xl md:text-3xl mx-1 opacity-80">:</span>
                <span className="inline-block min-w-[2ch]">{Math.floor((timeLeftMs % 60000) / 1000).toString().padStart(2, '0')}</span>
                <span className="text-2xl md:text-3xl mx-1 opacity-80">.</span>
                <span className="inline-block min-w-[3ch] text-3xl md:text-4xl">{Math.floor((timeLeftMs % 1000) / 10).toString().padStart(2, '0')}</span>
              </div>

              <div className="text-xs opacity-90">{t('payment.billing.offerEndingSoon')}</div>
            </div>
          </div>
        )}

        {/* 订阅计划选择 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6 max-w-5xl mx-auto pt-2">
          {SUBSCRIPTION_PLANS?.map((plan, index) => {
            const pricing = selectedPeriod === 'monthly' ? plan.monthly : plan.yearly;
            const originalPrice = pricing.originalPrice;
            const isInDiscount = showPromoOffer && timeLeft > 0;
            const currentPrice = plan.tier === 'trial' ? 0 : (isInDiscount ? pricing.discountPrice || originalPrice : originalPrice);
            const savedAmount = isInDiscount ? (originalPrice - currentPrice) : 0;
            const isSelected = selectedPlan?.id === plan.id;
            const yearlySavings = getYearlySavings(plan);

            // 检查是否应该禁用计划（当前用户等级高于此计划等级）
            const tierLevels = { trial: 0, pro: 1, premium: 2 };
            const currentLevel = tierLevels[userCurrentTier] || 0;
            const planLevel = tierLevels[plan.tier as keyof typeof tierLevels] || 0;
            const isDowngrade = currentLevel > planLevel;

            return (
              <div key={plan.id} className="relative pt-4">
                <Card
                  className={`transition-all duration-300 relative group w-full flex flex-col rounded-lg payment-card h-full min-h-[600px] ${
                    isDowngrade
                      ? 'opacity-50 cursor-not-allowed border-border bg-gray-50'
                      : isSelected
                      ? 'border-primary shadow-lg scale-105 bg-primary/5 cursor-pointer'
                      : 'border-border hover:border-primary/50 hover:shadow-md hover:scale-102 cursor-pointer'
                  }`}
                  onClick={() => !isDowngrade && handlePlanSelect(plan)}
                >
                  {/* 标签容器 */}
                  <div className="absolute top-3 left-0 right-0 z-20">
                    <div className="flex justify-between items-start px-4">
                      {/* 左侧标签组 */}
                      <div className="flex flex-wrap gap-1">
                        {/* 推荐标签 */}
                        {plan.recommended && (
                          <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-background shadow-xl px-3 py-1 text-xs font-bold rounded-full border border-white flex items-center gap-1">
                            <Star className="h-3 w-3 fill-current" />
                            {t('payment.plans.recommended')}
                          </Badge>
                        )}

                        {/* 高级版标签 */}
                        {plan.premiumLabel && (
                          <Badge className="bg-gradient-to-r from-purple-500 to-indigo-500 text-background shadow-xl px-3 py-1 text-xs font-bold rounded-full border border-white flex items-center gap-1">
                            <Crown className="h-3 w-3 fill-current" />
                            {selectedPeriod === 'yearly' ? t('payment.labels.savings') : t('payment.labels.premium')}
                          </Badge>
                        )}

                      </div>

                      {/* 右侧标签组 */}
                      <div className="flex flex-wrap gap-1">
                        {/* 限时优惠标签 */}
                        {showPromoOffer && timeLeft > 0 && plan.tier !== 'trial' && (
                          <Badge className="bg-gradient-to-r from-red-500 to-pink-500 text-background shadow-lg px-3 py-1 text-xs animate-pulse rounded-full border border-white flex items-center gap-1">
                            <Zap className="h-3 w-3 fill-current" />

                          </Badge>
                        )}

                        {/* 年付优惠标签 */}
                        {selectedPeriod === 'yearly' && (plan.tier === 'pro' || plan.tier === 'premium') && (
                          <Badge className="bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-lg px-3 py-1 text-xs rounded-full border border-white">
                            {t('payment.labels.yearlyCompareBadge', { savings: getYearlySavings(plan), percent: getYearlySavingsPercent(plan) })}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <CardHeader className="text-center pb-2 pt-4">
                    <CardTitle className="text-xl md:text-2xl font-bold mb-1 h-8 flex items-center justify-center">
                      <div className="flex items-center justify-center gap-2">
                        {plan.tier === 'premium' && <Crown className="h-5 w-5 text-warning" />}
                        <span className="text-foreground">{plan.name}</span>
                      </div>
                    </CardTitle>
                    <p className="text-muted-foreground text-xs md:text-sm leading-relaxed h-6 flex items-center justify-center">{plan.description}</p>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col space-y-2 pb-3 px-4">
                    <div className="text-center pricing-container payment-pricing flex flex-col justify-center">
                      <div className="space-y-2">
                        <div className="text-3xl md:text-4xl font-bold text-foreground flex items-baseline justify-center gap-1">
                          <span className="text-xl md:text-2xl">¥</span>
                          <span>{currentPrice}</span>
                          <span className="text-base text-muted-foreground font-medium">/{selectedPeriod === 'monthly' ? t('payment.billing.month') : t('payment.billing.year')}</span>
                          {selectedPeriod === 'monthly' ? (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {t('payment.labels.originalShort', { price: ORIGINAL_MONTHLY_PRICE[plan.tier as 'trial' | 'pro' | 'premium'] })}
                            </span>
                          ) : (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {t('payment.labels.originalShort', { price: ((ORIGINAL_MONTHLY_PRICE[plan.tier as 'trial' | 'pro' | 'premium']) || 0) * 12 })}
                            </span>
                          )}
                        </div>
                        {showPromoOffer && timeLeft > 0 && plan.tier !== 'trial' && (
                          <>
                            <div className="bg-gradient-to-r from-red-500 to-pink-500 text-background px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1">
                              <Zap className="h-4 w-4" />
                              {t('payment.promotion.specialOffer', { amount: savedAmount.toFixed(2) })}
                            </div>
                            <div className="text-lg text-muted-foreground line-through">$</div>
                          </>
                        )}

                      </div>
                    </div>
                    <div className="flex-1 space-y-1 mt-2 payment-features">
                      {plan.features.map((feature, index) => {
                        // 解析功能标记
                        let featureText = feature;
                        let badgeType = null;

                        if (feature.includes('|new')) {
                          featureText = feature.replace('|new', '');
                          badgeType = 'new';
                        } else if (feature.includes('|up')) {
                          featureText = feature.replace('|up', '');
                          badgeType = 'up';
                        }

                        return (
                          <div key={index} className="flex items-start gap-2 text-sm">
                            <div className="mt-0.5">
                              <Check className="h-3.5 w-3.5 text-success flex-shrink-0" />
                            </div>
                            <span className="text-gray-700 leading-snug flex items-center gap-1.5 flex-wrap">
                              {featureText}
                              {badgeType === 'new' && (
                                <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-background text-xs px-1.5 py-0.5 rounded-full font-bold shadow-sm">
                                  NEW
                                </Badge>
                              )}
                              {badgeType === 'up' && (
                                <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-background text-xs px-1.5 py-0.5 rounded-full font-bold shadow-sm flex items-center gap-0.5">
                                  <TrendingUp className="h-2.5 w-2.5" />
                                  UP
                                </Badge>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-1 flex justify-center">
                      <Button
                        variant={plan.recommended ? "default" : "default"}
                        size="lg"
                        className={`font-semibold transition-all duration-300 flex items-center justify-center text-center px-8 py-3 ${
                          plan.recommended
                            ? 'shadow-lg hover:shadow-xl hover:-translate-y-1'
                            : 'shadow-md hover:shadow-lg hover:-translate-y-0.5'
                        } ${
                          plan.tier === userCurrentTier
                            ? 'bg-primary text-primary-foreground hover:bg-primary/90 border-0'
                            : ''
                        }`}
                        disabled={isDowngrade}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDowngrade) {
                            handlePlanSelect(plan);
                          }
                        }}
                      >
                        {plan.tier === userCurrentTier && hasActiveSubscription && (() => {
                          // 获取用户当前订阅的周期信息
                          // 通过订阅到期时间推断订阅周期
                          if (primaryStatus?.expiresAt) {
                            const expiresAt = new Date(primaryStatus.expiresAt);
                            const now = new Date();
                            const diffDays = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

                            // 如果剩余天数超过200天，推断为年付订阅
                            const isYearlySubscription = diffDays > 200;
                            const isMatchingPeriod = isYearlySubscription ? selectedPeriod === 'yearly' : selectedPeriod === 'monthly';

                            return isMatchingPeriod;
                          }
                          return false; // 无法确定周期时不显示当前版本
                        })() ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            {t('payment.labels.currentPlan')}
                          </>
                        ) : isDowngrade ? (
                          <>
                            <Check className="w-4 h-4 mr-2 opacity-50" />
                            {t('payment.plans.alreadyHaveHigher')}
                          </>
                        ) : isSelected ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            {t('payment.plans.selected')}
                          </>
                        ) : (
                          <>
                            <Crown className="w-4 h-4 mr-2" />
                            {t('payment.plans.selectPlan')}
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>

        {/* 支付信息和二维码 */}
        <div id="payment-section" ref={paymentInfoRef} className="space-y-6 max-w-4xl mx-auto">
          {/* 支付按钮 */}
          {selectedPlan && selectedPlan.tier !== 'trial' && !showQRCode && (
            <Card className="border border-border bg-card shadow-sm rounded-lg">
              <CardContent className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                      <CreditCard className="h-5 w-5 text-background" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">{t('payment.paymentInfo.title')}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-foreground">¥{getCurrentPrice()}</div>
                    <div className="text-sm text-muted-foreground font-medium">{selectedPeriod === 'monthly' ? t('payment.billing.monthlyShort') : t('payment.billing.yearlyShort')}</div>
                    <div className="text-xs text-muted-foreground">
                      {t('payment.labels.originalShort', { price: selectedPlan ? (selectedPeriod === 'monthly' ? ORIGINAL_MONTHLY_PRICE[selectedPlan.tier as 'trial' | 'pro' | 'premium'] : ((ORIGINAL_MONTHLY_PRICE[selectedPlan.tier as 'trial' | 'pro' | 'premium'] || 0) * 12)) : 0 })}
                    </div>
                    {dynamicPricing && (
                      <div className="text-xs text-primary mt-1">{dynamicPricing.priceDescription}</div>
                    )}
                  </div>
                </div>

                {/* 金额提醒区域 - 当价格与原价相差超过1元时显示 */}
                {dynamicPricing && Math.abs(dynamicPricing.finalAmount - dynamicPricing.originalPrice) > 1 && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-800 mb-2">
                      <AlertCircle className="h-4 w-4" />
                      <span className="font-medium">支付金额提醒</span>
                    </div>
                    <p className="text-sm text-blue-700 mb-1">
                      请在支付宝中手动输入金额：<span className="font-bold text-lg text-blue-900">¥{dynamicPricing.finalAmount}</span>
                    </p>
                    {dynamicPricing.priceType === 'prorated' && (
                      <p className="text-xs text-primary">
                        补差价计算：根据您的剩余订阅时间计算的升级费用
                      </p>
                    )}
                    {dynamicPricing.discountAmount > 0 && (
                      <p className="text-xs text-primary">
                        已为您节省：¥{dynamicPricing.discountAmount.toFixed(2)}
                      </p>
                    )}
                  </div>
                )}

                <div className="mb-6 p-4 bg-muted/30 border border-border rounded-lg shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-lg font-bold text-foreground">{selectedPlan.name}</span>
                      <span className="text-sm text-muted-foreground ml-3">{selectedPlan.description}</span>
                    </div>
                  </div>
                </div>

                {/* 按年支付引导 */}
                {selectedPeriod === 'monthly' && selectedPlan && (selectedPlan.tier as any) !== 'trial' && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center">
                          <Percent className="h-4 w-4 text-background" />
                        </div>
                        <div>
                          <div className="font-semibold text-orange-800"></div>
                          <div className="text-sm text-orange-600">
                            {t('payment.yearlyPromotion.description', { savings: getYearlySavings(selectedPlan), months: Math.round(getYearlySavings(selectedPlan) / (selectedPlan.monthly.originalPrice || 0)) })}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedPeriod('yearly')}
                        className="bg-gradient-to-r from-orange-500 to-yellow-500 text-background border-none hover:from-orange-600 hover:to-yellow-600 font-semibold"
                      >

                      </Button>
                    </div>
                  </div>
                )}

                {/* 动态价格信息显示 */}
                {dynamicPricing && dynamicPricing.discountAmount > 0 && (
                  <div className="flex flex-wrap gap-3 mb-6">
                    {dynamicPricing.priceType === 'promo' && (
                      <span className="bg-gradient-to-r from-red-500 to-pink-500 text-background px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1">
                        <Zap className="h-4 w-4" />
                        限时优惠：节省¥{dynamicPricing.discountAmount.toFixed(2)}
                      </span>
                    )}
                    {dynamicPricing.priceType === 'prorated' && (
                      <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-background px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1">
                        <TrendingUp className="h-4 w-4" />
                        补差价升级：节省¥{dynamicPricing.discountAmount.toFixed(2)}
                      </span>
                    )}
                    {selectedPeriod === 'yearly' && dynamicPricing.priceType === 'original' && (
                      <span className="bg-gradient-to-r from-primary to-secondary text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1">
                        <Percent className="h-4 w-4" />
                        年付优惠：节省¥{getYearlySavings(selectedPlan)}
                      </span>
                    )}
                  </div>
                )}

                <Button
                  onClick={handlePayment}
                  disabled={isCreatingCheckout}
                  className="w-full !flex !items-center !justify-center !bg-gradient-to-r !from-blue-600 !to-indigo-600 hover:!from-blue-700 hover:!to-indigo-700 !text-background text-xl font-bold py-4 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50 !border-none !text-center"
                >
                  {isCreatingCheckout ? (
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-5 w-5 animate-spin" />
                      {t('payment.actions.creating')}
                    </div>
                  ) : (
                    t('payment.actions.payNow', { amount: getCurrentPrice() })
                  )}
                </Button>

                {/* 支付错误提示 */}
                {checkoutError && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center gap-2 text-destructive">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium"></span>
                    </div>
                    <p className="text-destructive text-sm mt-1">{checkoutError}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCheckoutError(null)}
                      className="mt-2"
                    >

                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* 支付二维码 */}
          {showQRCode && selectedPlan && selectedPlan.tier !== 'trial' && (
            <Card className="border border-border bg-card shadow-sm rounded-lg">
              <CardContent className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-background text-lg font-bold">支</span>
                    </div>
                    <h3 className="text-xl font-bold text-foreground"></h3>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-foreground">¥{getCurrentPrice()}</div>
                    <div className="text-sm text-muted-foreground font-medium">{selectedPeriod === 'monthly' ? t('payment.billing.monthlyShort') : t('payment.billing.yearlyShort')}</div>
                    <div className="text-xs text-muted-foreground">
                      {t('payment.labels.originalShort', { price: selectedPlan ? (selectedPeriod === 'monthly' ? ORIGINAL_MONTHLY_PRICE[selectedPlan.tier as 'trial' | 'pro' | 'premium'] : ((ORIGINAL_MONTHLY_PRICE[selectedPlan.tier as 'trial' | 'pro' | 'premium'] || 0) * 12)) : 0 })}
                    </div>
                    {dynamicPricing && (
                      <div className="text-xs text-primary mt-1">{dynamicPricing.priceDescription}</div>
                    )}
                  </div>
                </div>

                {/* 支付金额提醒（在二维码上方） */}
                {dynamicPricing && Math.abs(dynamicPricing.finalAmount - dynamicPricing.originalPrice) > 1 && (
                  <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2 text-orange-800 mb-2">
                      <AlertCircle className="h-4 w-4" />
                      <span className="font-medium">重要提醒</span>
                    </div>
                    <p className="text-sm text-orange-700 mb-1">
                      请在支付宝中手动输入金额：<span className="font-bold text-xl text-orange-900">¥{dynamicPricing.finalAmount}</span>
                    </p>
                    {dynamicPricing.priceType === 'prorated' && (
                      <p className="text-xs text-orange-600">
                        补差价升级：基于您的剩余订阅时间计算
                      </p>
                    )}
                    {dynamicPricing.discountAmount > 0 && (
                      <p className="text-xs text-orange-600">
                        为您节省了 ¥{dynamicPricing.discountAmount.toFixed(2)}
                      </p>
                    )}
                  </div>
                )}

                {/* 优惠信息行 */}
                {(showPromoOffer && timeLeft > 0) || selectedPeriod === 'yearly' ? (
                  <div className="flex flex-wrap gap-3 mb-6">
                    {showPromoOffer && timeLeft > 0 && (
                      <span className="bg-gradient-to-r from-red-500 to-pink-500 text-background px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1">
                        <Zap className="h-4 w-4" />
                        时优惠中
                      </span>
                    )}
                    {selectedPeriod === 'yearly' && (
                      <span className="bg-gradient-to-r from-primary to-secondary text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1">
                        <Percent className="h-4 w-4" />
                        优惠
                      </span>
                    )}
                  </div>
                ) : null}

                <div className="flex justify-center items-center py-6 bg-background rounded-xl border-2 border-border shadow-sm">
                  {bufpayPaymentInfo && bufpayOrderId ? (
                    <PaymentQRCode
                      paymentInfo={bufpayPaymentInfo}
                      orderId={bufpayOrderId}
                      onPaymentSuccess={handleBufpaySuccess}
                      onPaymentTimeout={handleBufpayTimeout}
                      onPaymentError={handleBufpayError}
                    />
                  ) : (
                    <div className="text-destructive font-semibold"></div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* BufPay 支付状态会通过 PaymentQRCode 组件内部轮询处理 */}

        </div>
      </div>

      {/* 升级对话框 */}
      {selectedPlan && (
        <SubscriptionUpgradeDialog
          open={showUpgradeDialog}
          onOpenChange={setShowUpgradeDialog}
          targetTier={selectedPlan.tier}
          targetPeriod={selectedPeriod}
          onUpgradeSuccess={() => {
            toast({
              title: t('pages.labels.升级成功'),
              description: '您的订阅已成功升级！',
            });
            // 刷新页面或重新获取用户信息
            window.location.reload();
          }}
        />
      )}

      {/* 支付模态框 */}
      {showPaymentModal && paymentModalData && (
        <PaymentModal
          open={showPaymentModal}
          onClose={handlePaymentModalClose}
          paymentData={paymentModalData}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentFailed={handlePaymentFailed}
          onPaymentTimeout={handleBufpayTimeout}
          onCancel={handlePaymentCancel}
        />
      )}
    </div>
  );
}

// ✅ FIXED: JSX语法错误已修复，背景颜色优化已完成
